import { Product } from '@shared/components/ui/Product';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@shared/components/ui/shadcn/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useToast } from '@shared/hooks/use-toast';
import { cn } from '@shared/utils/utils';
import { Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateProductCost, useDeleteProductCost, useProductCosts, useUpdateProductCost } from '../../hooks';
import type { ProfitabilityProductCost } from '../../models/Profitability';
import { formatProfitabilityAmount } from '../../utils/formatMoney';
import { getProfitabilityMutationErrorMessage } from '../../utils/profitabilityApiError';
import { formatProductCostEffectiveFrom, sortProductCosts } from '../../utils/productCostsDisplay';

interface CostDraft {
  cost: string;
  effectiveFrom: string;
}

export interface ProductCostsModalProduct {
  id: number;
  name: string;
  code?: string | null;
  image_url?: string | null;
}

interface ProductCostsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ProductCostsModalProduct | null;
  locale: string;
  currency: string;
  onSaved?: () => void;
}

function emptyDraft(): CostDraft {
  return { cost: '', effectiveFrom: '' };
}

function draftFromRow(row: ProfitabilityProductCost): CostDraft {
  return {
    cost: String(row.cost),
    effectiveFrom: row.effective_from ?? '',
  };
}

function EffectiveFromField({
  requiresDate,
  value,
  onChange,
  baseCostLabel,
  effectiveFromLabel,
  testId,
}: {
  requiresDate: boolean;
  value: string;
  onChange: (value: string) => void;
  baseCostLabel: string;
  effectiveFromLabel: string;
  testId: string;
}) {
  if (!requiresDate) {
    return <span className="text-xs text-muted-foreground">{baseCostLabel}</span>;
  }

  return (
    <Input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-[140px] border-border bg-background text-xs"
      aria-label={effectiveFromLabel}
      data-testid={testId}
    />
  );
}

export const ProductCostsModal = ({
  open,
  onOpenChange,
  product,
  locale,
  currency,
  onSaved,
}: ProductCostsModalProps) => {
  const { t } = useTranslation('profitabilityModule');
  const { toast } = useToast();
  const baseCostLabel = t('product_costs.base_cost_label');

  const productId = product?.id ?? null;
  const costsQuery = useProductCosts(open && productId != null, productId);
  const createM = useCreateProductCost();
  const updateM = useUpdateProductCost();
  const deleteM = useDeleteProductCost();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<CostDraft>(emptyDraft());
  const [newDraft, setNewDraft] = useState<CostDraft>(emptyDraft());
  const [pendingDelete, setPendingDelete] = useState<ProfitabilityProductCost | null>(null);

  const costs = useMemo(() => sortProductCosts(costsQuery.data?.data ?? []), [costsQuery.data?.data]);

  const saving = createM.isPending || updateM.isPending;
  const newRequiresDate = costs.length > 0;
  const editingRow = editingId != null ? costs.find((c) => c.id === editingId) : null;
  const editRequiresDate = editingRow?.effective_from != null;

  const resetNewDraft = useCallback(() => {
    setNewDraft(emptyDraft());
  }, []);

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setEditDraft(emptyDraft());
      setNewDraft(emptyDraft());
      setPendingDelete(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && editingId == null) {
      resetNewDraft();
    }
  }, [open, costs.length, editingId, resetNewDraft]);

  const buildPayload = (draft: CostDraft, requiresDate: boolean) => {
    if (!product) return null;

    const cost = parseFloat(draft.cost.replace(',', '.'));
    if (!Number.isFinite(cost) || cost < 0) {
      toast({
        title: t('product_costs.toast.validation'),
        description: t('product_costs.validation_cost'),
        variant: 'destructive',
      });
      return null;
    }

    const payload: {
      product_id: number;
      cost: number;
      effective_from?: string | null;
    } = { product_id: product.id, cost };

    if (!requiresDate) {
      payload.effective_from = null;
    } else {
      const date = draft.effectiveFrom.trim();
      if (!date) {
        toast({
          title: t('product_costs.toast.validation'),
          description: t('product_costs.validation_effective_from'),
          variant: 'destructive',
        });
        return null;
      }
      payload.effective_from = date;
    }

    return payload;
  };

  const handleCreate = async () => {
    const payload = buildPayload(newDraft, newRequiresDate);
    if (!payload) return;

    try {
      await createM.mutateAsync(payload);
      toast({ title: t('product_costs.toast.created') });
      resetNewDraft();
      onSaved?.();
    } catch (err) {
      toast({
        title: t('product_costs.toast.error'),
        description: getProfitabilityMutationErrorMessage(err, t('errors.generic')),
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async () => {
    if (editingId == null) return;
    const payload = buildPayload(editDraft, editRequiresDate);
    if (!payload) return;

    try {
      await updateM.mutateAsync({ id: editingId, body: payload });
      toast({ title: t('product_costs.toast.updated') });
      setEditingId(null);
      onSaved?.();
    } catch (err) {
      toast({
        title: t('product_costs.toast.error'),
        description: getProfitabilityMutationErrorMessage(err, t('errors.generic')),
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (row: ProfitabilityProductCost) => {
    setEditingId(row.id);
    setEditDraft(draftFromRow(row));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(emptyDraft());
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteM.mutateAsync(pendingDelete.id);
      toast({ title: t('product_costs.toast.deleted') });
      if (editingId === pendingDelete.id) cancelEdit();
      setPendingDelete(null);
      onSaved?.();
    } catch (err) {
      toast({
        title: t('product_costs.toast.error'),
        description: getProfitabilityMutationErrorMessage(err, t('errors.generic')),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-h-[90vh] max-w-2xl overflow-y-auto border-border bg-background"
          data-testid="profitability-product-costs-modal"
        >
          <DialogHeader>
            <DialogTitle>{t('product_costs.modal_title')}</DialogTitle>
          </DialogHeader>

          {product && (
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              <Product
                id={product.id}
                name={product.name}
                code={product.code ?? undefined}
                src={product.image_url?.trim() ? product.image_url : ''}
                className="min-w-0 max-w-full justify-start"
                showTooltip
                showImageModal={Boolean(product.image_url?.trim())}
                data-testid="profitability-product-costs-modal-product"
              />
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="w-[120px] text-muted-foreground">{t('product_costs.col_cost')}</TableHead>
                  <TableHead className="text-muted-foreground">{t('product_costs.col_effective_from')}</TableHead>
                  <TableHead className="w-[96px] text-muted-foreground">{t('product_costs.col_actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costsQuery.isLoading && (
                  <TableRow>
                    <TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">
                      …
                    </TableCell>
                  </TableRow>
                )}

                {!costsQuery.isLoading &&
                  costs.map((row) => {
                    const isEditingRow = editingId === row.id;

                    if (isEditingRow) {
                      return (
                        <TableRow key={row.id} className="border-border bg-muted/40">
                          <TableCell>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={editDraft.cost}
                              onChange={(e) => setEditDraft((d) => ({ ...d, cost: e.target.value }))}
                              placeholder="0.00"
                              className="h-8 w-full max-w-[120px] border-border bg-background text-sm"
                              data-testid={`profitability-product-cost-modal-edit-cost-${row.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <EffectiveFromField
                              requiresDate={editRequiresDate}
                              value={editDraft.effectiveFrom}
                              onChange={(effectiveFrom) => setEditDraft((d) => ({ ...d, effectiveFrom }))}
                              baseCostLabel={baseCostLabel}
                              effectiveFromLabel={t('product_costs.field_effective_from')}
                              testId={`profitability-product-cost-edit-effective-from-${row.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                disabled={saving}
                                onClick={handleUpdate}
                                title={t('product_costs.save')}
                                data-testid={`profitability-product-cost-modal-save-${row.id}`}
                              >
                                <Save className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={cancelEdit}
                                title={t('product_costs.cancel_edit')}
                                data-testid={`profitability-product-cost-modal-cancel-${row.id}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return (
                      <TableRow key={row.id} className="border-border">
                        <TableCell className="tabular-nums text-sm font-medium">
                          {formatProfitabilityAmount(row.cost, locale, currency)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatProductCostEffectiveFrom(row.effective_from, locale, baseCostLabel)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              disabled={editingId != null}
                              onClick={() => handleEdit(row)}
                              title={t('product_costs.edit')}
                              data-testid={`profitability-product-cost-modal-edit-${row.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              disabled={editingId != null}
                              onClick={() => setPendingDelete(row)}
                              title={t('product_costs.delete')}
                              data-testid={`profitability-product-cost-modal-delete-${row.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                {!costsQuery.isLoading && (
                  <TableRow
                    className={cn('border-border bg-muted/10')}
                    data-testid="profitability-product-cost-modal-new-row"
                  >
                    <TableCell>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={newDraft.cost}
                        onChange={(e) => setNewDraft((d) => ({ ...d, cost: e.target.value }))}
                        placeholder="0.00"
                        disabled={editingId != null || saving}
                        className="h-8 w-full max-w-[120px] border-border bg-background text-sm"
                        data-testid="profitability-product-cost-modal-new-cost"
                      />
                    </TableCell>
                    <TableCell>
                      <EffectiveFromField
                        requiresDate={newRequiresDate}
                        value={newDraft.effectiveFrom}
                        onChange={(effectiveFrom) => setNewDraft((d) => ({ ...d, effectiveFrom }))}
                        baseCostLabel={baseCostLabel}
                        effectiveFromLabel={t('product_costs.field_effective_from')}
                        testId="profitability-product-cost-new-effective-from"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={editingId != null || saving || !product}
                        onClick={handleCreate}
                        title={t('product_costs.add')}
                        data-testid="profitability-product-cost-modal-add"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <DialogContent className="max-w-md border-border bg-background">
          <DialogHeader>
            <DialogTitle>{t('product_costs.delete_confirm_title')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('product_costs.delete_confirm_body')}</p>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setPendingDelete(null)} className="border-border">
              {t('product_costs.delete_cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteM.isPending}
              onClick={confirmDelete}
              data-testid="profitability-product-cost-modal-confirm-delete"
            >
              {t('product_costs.delete_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
